import apiManager from "utils/api/apiManager";
import { create } from "zustand";

type InitialState = {
  historyLoading: boolean;
  askLoading: boolean;
  answer: string | null;
  messages: {
    id: string;
    type: "human" | "ai";
    content: string;
    date?: string;
  }[];
  loadHistory: () => Promise<void>;
  reset: () => void;
  ask: (id: string) => Promise<boolean>;
};

const initialValues = {
  historyLoading: false,
  askLoading: false,
  answer: null,
  messages: [] as {
    id: string;
    type: "human" | "ai";
    content: string;
    date?: string;
  }[],
};

export const useChatBot = create<InitialState>((set, get) => ({
  ...initialValues,

  ask: async (question: string) => {
    const q = (question || "").trim();
    if (!q) return false;
    // Immediately append human message so it appears without waiting
    set(
      (prev) =>
        ({
          askLoading: true,
          messages: [
            ...prev.messages,
            {
              id: `temp-${Date.now()}`,
              type: "human",
              content: q,
              date: new Date().toISOString(),
            },
          ],
        } as any)
    );
    try {
      const res = await apiManager.postData("api/AI/chatbot/ask", {
        question: q,
      });
      const answer =
        !res.isError && (res as any)?.response?.answer
          ? (res as any).response.answer
          : null;
      if (answer) {
        set(
          (prev) =>
            ({
              answer,
              messages: [
                ...prev.messages,
                {
                  id: (res as any)?.response?.id || `a-${Date.now()}`,
                  type: "ai",
                  content: answer,
                  date: new Date().toISOString(),
                },
              ],
            } as any)
        );
      } else if (res.isError) {
        const errMsg = (res as any).errorMessage || "Error";
        set(
          (prev) =>
            ({
              answer: null,
              messages: [
                ...prev.messages,
                {
                  id: `err-${Date.now()}`,
                  type: "ai",
                  content: errMsg,
                  date: new Date().toISOString(),
                },
              ],
            } as any)
        );
      }
      return !res.isError;
    } catch (err) {
      set(
        (prev) =>
          ({
            messages: [
              ...prev.messages,
              {
                id: `err-${Date.now()}`,
                type: "ai",
                content: "Request failed",
                date: new Date().toISOString(),
              },
            ],
          } as any)
      );
      return false;
    } finally {
      set({ askLoading: false });
    }
  },

  loadHistory: async () => {
    set({ historyLoading: true });
    try {
      const res = await apiManager.getData<any[]>("api/AI/chatbot/history");
      console.log(res);
      if (!res?.isError && Array.isArray(res?.response)) {
        const mapped = (res.response as any[]).map((m: any, idx: number) => ({
          id: String(m.id || `h-${idx}-${Date.now()}`),
          type: m.type === "ai" ? ("ai" as const) : ("human" as const),
          content: String(m.content || ""),
          date: m.date ? String(m.date) : undefined,
        }));
        setTimeout(() => {
          set({ messages: mapped as any });
        }, 400);
      }
    } catch (e) {
      // ignore
    } finally {
      setTimeout(() => {
        set({ historyLoading: false });
      }, 400);
    }
  },

  reset: () => {
    set(initialValues);
  },
}));
